import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useEntity } from "@/context/EntityContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, Trash2, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

// iter 72{ad} — Fixed Asset Wizard. Guided flow that:
//   1. Captures the asset (name, cost, purchase date, useful life, salvage,
//      Asset CoA account, paid-from wallet).
//   2. Previews the monthly straight-line depreciation schedule live.
//   3. On submit, posts the acquisition JE (DR Asset / CR Cash-or-Card)
//      and stores the schedule so the user can post each month's
//      depreciation with one click.
//
// Business-only surface — Personal workspaces don't see this page.

export default function FixedAssets() {
  const { currentEntity } = useEntity();
  const navigate = useNavigate();
  const isBusiness = currentEntity?.workspace_type === "business";

  const [assets, setAssets] = useState([]);
  const [coa, setCoa] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isBusiness) return;
    setLoading(true);
    try {
      const [a, c, w] = await Promise.all([
        api.get("/fixed-assets"),
        api.get("/coa"),
        api.get("/accounts"),
      ]);
      setAssets(a.data || []);
      setCoa(c.data || []);
      setWallets(w.data || []);
    } catch (err) {
      console.warn("[FixedAssets] load failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [isBusiness]);
  useEffect(() => { load(); }, [load]);

  const postMonth = async (fa) => {
    try {
      const { data } = await api.post(`/fixed-assets/${fa.id}/post-depreciation`);
      toast.success(`Posted ${data.month} depreciation of ${formatCurrency(data.amount, fa.currency)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post depreciation");
    }
  };

  const removeAsset = async (fa) => {
    if (!window.confirm(`Delete '${fa.name}'? This removes the acquisition JE and every posted depreciation.`)) return;
    try {
      await api.delete(`/fixed-assets/${fa.id}`);
      toast.success("Fixed asset deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (!isBusiness) {
    return (
      <div className="p-8">
        <div className="max-w-lg mx-auto text-center py-16">
          <Building2 className="w-12 h-12 mx-auto text-[var(--muted-text)]" />
          <h2 className="mt-4 text-xl font-display">Fixed Assets are a Business feature</h2>
          <p className="mt-2 text-sm text-[var(--muted-text)]">
            Switch to a Business workspace from the top-left picker to record equipment,
            vehicles, and other capitalized purchases.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="fixed-assets-page" className="p-6 max-w-6xl mx-auto space-y-6">
      {/* iter 72ag — top-of-page navigation back to Dashboard so users
          returning from a successful FA post have a one-tap exit. */}
      <Button data-testid="fixed-assets-back-btn"
              variant="ghost" size="sm"
              onClick={() => navigate("/")}
              className="text-[var(--muted-text)] hover:text-[var(--brand-text)] -ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
      </Button>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--muted-text)]">Assets</div>
          <h1 className="font-display text-3xl mt-1">Fixed Assets</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Equipment, vehicles, buildings — anything capitalized and depreciated over time.
          </p>
        </div>
        <Button data-testid="add-fixed-asset-btn"
                onClick={() => setWizardOpen(true)}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">
          <Plus className="w-4 h-4 mr-1" /> New fixed asset
        </Button>
      </div>

      {loading && <div className="text-sm text-[var(--muted-text)]">Loading…</div>}

      {!loading && assets.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[var(--divider)] py-16 text-center"
             data-testid="fixed-assets-empty">
          <Building2 className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <p className="mt-3 text-sm text-[var(--muted-text)]">No fixed assets yet.</p>
          <Button variant="link" onClick={() => setWizardOpen(true)}
                  className="mt-1 text-[#2F4F4F]" data-testid="fixed-assets-empty-cta">
            Add your first asset <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {assets.map((fa) => (
          <div key={fa.id} data-testid={`fixed-asset-card-${fa.id}`}
               className="rounded-2xl border border-[var(--divider)] bg-[var(--card)] p-4 space-y-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="font-semibold text-base">{fa.name}</div>
                <div className="text-[11px] text-[var(--muted-text)]">
                  {fa.asset_coa_code} · purchased {fa.purchase_date}
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-[#B4283A]"
                      data-testid={`fixed-asset-delete-${fa.id}`}
                      onClick={() => removeAsset(fa)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Cost</div>
                <div className="font-mono">{formatCurrency(fa.cost, fa.currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Book value</div>
                <div className="font-mono">{formatCurrency(fa.book_value, fa.currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Accumulated depr.</div>
                <div className="font-mono">{formatCurrency(fa.accumulated_depreciation, fa.currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Last posted</div>
                <div className="font-mono">{fa.last_depreciated_month || "—"}</div>
              </div>
            </div>
            <Button size="sm" data-testid={`fixed-asset-post-${fa.id}`}
                    className="w-full rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                    onClick={() => postMonth(fa)}>
              Post next depreciation
            </Button>
          </div>
        ))}
      </div>

      <FixedAssetWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        coa={coa}
        wallets={wallets}
        onSaved={() => { setWizardOpen(false); load(); }}
      />
    </div>
  );
}

function FixedAssetWizard({ open, onClose, coa, wallets, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "",
    cost: "",
    purchase_date: today,
    useful_life_years: 5,
    salvage_value: 0,
    depreciation_method: "straight_line",
    asset_coa_code: "",
    payment_account_id: "none",
    currency: "USD",
    notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const assetCoa = useMemo(
    () => (coa || []).filter((c) => c.type === "Asset" && c.is_active !== false),
    [coa],
  );

  // Live-preview the depreciation schedule as the user fills the form.
  useEffect(() => {
    if (!open) return;
    if (!(Number(form.cost) > 0 && Number(form.useful_life_years) > 0 && form.asset_coa_code)) {
      setPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/fixed-assets/preview-schedule", {
          ...form,
          cost: Number(form.cost),
          useful_life_years: Number(form.useful_life_years),
          salvage_value: Number(form.salvage_value || 0),
          payment_account_id: form.payment_account_id === "none" ? null : form.payment_account_id,
        });
        setPreview(data);
      } catch (err) {
        setPreview(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [open, form]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/fixed-assets", {
        ...form,
        cost: Number(form.cost),
        useful_life_years: Number(form.useful_life_years),
        salvage_value: Number(form.salvage_value || 0),
        payment_account_id: form.payment_account_id === "none" ? null : form.payment_account_id,
      });
      toast.success(`Fixed asset '${form.name}' created`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.name.trim() && Number(form.cost) > 0 && form.asset_coa_code && Number(form.useful_life_years) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl" data-testid="fixed-asset-wizard">
        <DialogHeader>
          <DialogTitle>New fixed asset</DialogTitle>
          <DialogDescription>
            Record an asset, its useful life, and salvage value. We'll post the acquisition JE and generate a straight-line depreciation schedule you can post monthly with one click.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-2">
            <Label>Asset name</Label>
            <Input data-testid="fa-name" placeholder="e.g. Delivery van, MacBook Pro"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Cost</Label>
            <Input data-testid="fa-cost" type="number" step="0.01" min="0"
                   value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Purchase date</Label>
            <Input data-testid="fa-purchase-date" type="date"
                   value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Useful life (years)</Label>
            <Input data-testid="fa-life" type="number" step="0.5" min="0.5"
                   value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Salvage value</Label>
            <Input data-testid="fa-salvage" type="number" step="0.01" min="0"
                   value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Asset CoA account</Label>
            <Select value={form.asset_coa_code} onValueChange={(v) => setForm({ ...form, asset_coa_code: v })}>
              <SelectTrigger data-testid="fa-coa"><SelectValue placeholder="Select an Asset account" /></SelectTrigger>
              <SelectContent>
                {assetCoa.length === 0 && (
                  <div className="px-3 py-2 text-xs text-[var(--muted-text)]">
                    No Asset accounts yet. Add one under Chart of Accounts (e.g. 1500 Equipment).
                  </div>
                )}
                {assetCoa.map((c) => (
                  <SelectItem key={c.id || c.code} value={c.code} data-testid={`fa-coa-${c.code}`}>
                    <span className="font-mono text-xs mr-2">{c.code}</span>{c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Paid from (optional)</Label>
            <Select value={form.payment_account_id} onValueChange={(v) => setForm({ ...form, payment_account_id: v })}>
              <SelectTrigger data-testid="fa-wallet"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Cash on hand (1000)</SelectItem>
                {(wallets || []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name} · {w.currency}{w.type === "card" ? " · Card" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="fa-notes" rows={2}
                      value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        {preview && (
          <div data-testid="fa-preview"
               className="rounded-xl border border-[var(--divider)] bg-[var(--card)] p-3 space-y-2">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
              <Sparkles className="w-3.5 h-3.5" /> Depreciation preview
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Depreciable base</div>
                <div className="font-mono">{formatCurrency(preview.depreciable_base, form.currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Monthly expense</div>
                <div className="font-mono">{formatCurrency(preview.monthly_expense, form.currency)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--muted-text)]">Total months</div>
                <div className="font-mono">{preview.total_months}</div>
              </div>
            </div>
            <div className="text-[10px] text-[var(--muted-text)]">
              First posting: {preview.schedule?.[0]?.month} · last posting: {preview.schedule?.[preview.schedule.length - 1]?.month}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="fa-cancel">Cancel</Button>
          <Button data-testid="fa-save" disabled={!canSave || saving} onClick={submit}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">
            {saving ? "Saving…" : "Create asset & post acquisition"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
